import type { ReactNode } from 'react';

/**
 * Tacómetro analógico SVG: aguja, arco rojo y marcha en el centro del cuadro.
 * El tacómetro es el instrumento que enseña CUÁNDO cambiar: va bien visible.
 */

const SWEEP_START = 150; // grados (abajo-izquierda), barrido horario
const SWEEP_DEG = 240;

function pt(angleDeg: number, r: number): [number, number] {
  const a = (angleDeg * Math.PI) / 180;
  return [60 + r * Math.cos(a), 60 + r * Math.sin(a)];
}

function arcPath(fromDeg: number, toDeg: number, r: number): string {
  const [x0, y0] = pt(fromDeg, r);
  const [x1, y1] = pt(toDeg, r);
  const large = toDeg - fromDeg > 180 ? 1 : 0;
  return `M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

export function Tacho({
  rpm,
  redlineRpm,
  gearLabel,
  gearAccent,
  shiftUp,
  shiftDown,
  dimmed,
}: {
  rpm: number;
  redlineRpm: number;
  gearLabel: string;
  /** Color del recuadro de marcha ('sky' normal, 'amber' para R, 'rose' calado). */
  gearAccent: 'sky' | 'amber' | 'rose';
  shiftUp: boolean;
  shiftDown: boolean;
  /** Motor parado: cuadro apagado. */
  dimmed: boolean;
}) {
  const maxShown = Math.ceil(redlineRpm / 1000) * 1000;
  const frac = Math.min(1.04, Math.max(0, rpm / maxShown));
  const needleDeg = SWEEP_START + frac * SWEEP_DEG;
  const redFrom = SWEEP_START + (redlineRpm / maxShown) * SWEEP_DEG;

  const ticks: ReactNode[] = [];
  for (let k = 0; k * 1000 <= maxShown; k++) {
    const a = SWEEP_START + ((k * 1000) / maxShown) * SWEEP_DEG;
    const [x0, y0] = pt(a, 43);
    const [x1, y1] = pt(a, 50);
    const red = k * 1000 >= redlineRpm;
    ticks.push(
      <line
        key={`t${k}`}
        x1={x0}
        y1={y0}
        x2={x1}
        y2={y1}
        stroke={red ? '#fb7185' : 'rgba(255,255,255,0.75)'}
        strokeWidth="2"
      />,
    );
    const [lx, ly] = pt(a, 34);
    ticks.push(
      <text
        key={`l${k}`}
        x={lx}
        y={ly + 3}
        textAnchor="middle"
        fontSize="8.5"
        fontWeight="700"
        fill={red ? '#fb7185' : 'rgba(255,255,255,0.8)'}
      >
        {k}
      </text>,
    );
    // Marca intermedia (cada 500 rpm)
    if ((k + 0.5) * 1000 <= maxShown) {
      const am = SWEEP_START + (((k + 0.5) * 1000) / maxShown) * SWEEP_DEG;
      const [mx0, my0] = pt(am, 46.5);
      const [mx1, my1] = pt(am, 50);
      ticks.push(
        <line key={`m${k}`} x1={mx0} y1={my0} x2={mx1} y2={my1} stroke="rgba(255,255,255,0.35)" strokeWidth="1.2" />,
      );
    }
  }

  const gearBg =
    gearAccent === 'rose' ? 'rgba(244,63,94,0.92)' : gearAccent === 'amber' ? '#fbbf24' : '#38bdf8';

  return (
    <svg viewBox="0 0 120 120" className="h-32 w-32" style={{ opacity: dimmed ? 0.55 : 1 }}>
      <circle cx="60" cy="60" r="56" fill="rgba(2,6,23,0.55)" />
      <path d={arcPath(SWEEP_START, SWEEP_START + SWEEP_DEG, 50)} stroke="rgba(255,255,255,0.16)" strokeWidth="6" fill="none" />
      <path d={arcPath(redFrom, SWEEP_START + SWEEP_DEG, 50)} stroke="rgba(244,63,94,0.85)" strokeWidth="6" fill="none" />
      {ticks}
      {/* Flechas GSI: conviene subir / bajar marcha */}
      {shiftUp && (
        <path d="M 60 16 l 6 9 h -12 z" fill="#34d399">
          <animate attributeName="opacity" values="1;0.3;1" dur="0.9s" repeatCount="indefinite" />
        </path>
      )}
      {shiftDown && (
        <path d="M 60 25 l 6 -9 h -12 z" fill="#38bdf8">
          <animate attributeName="opacity" values="1;0.3;1" dur="0.9s" repeatCount="indefinite" />
        </path>
      )}
      {/* Aguja */}
      <g
        style={{
          transform: `rotate(${needleDeg}deg)`,
          transformOrigin: '60px 60px',
          transition: 'transform 90ms linear',
        }}
      >
        <line x1="60" y1="60" x2="105" y2="60" stroke="#fb7185" strokeWidth="2.6" strokeLinecap="round" />
        <line x1="60" y1="60" x2="52" y2="60" stroke="#fb7185" strokeWidth="3.4" strokeLinecap="round" />
      </g>
      <circle cx="60" cy="60" r="5" fill="#0f172a" stroke="rgba(255,255,255,0.5)" strokeWidth="1.4" />
      {/* rpm digital y marcha en el hueco inferior del dial */}
      <text x="60" y="79" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.55)" fontFamily="ui-monospace, monospace">
        {Math.round(rpm)} rpm
      </text>
      <rect x="46" y="84" width="28" height="26" rx="6" fill={dimmed ? 'rgba(148,163,184,0.35)' : gearBg} />
      <text
        x="60"
        y="103"
        textAnchor="middle"
        fontSize="19"
        fontWeight="900"
        fill="#020617"
        fontFamily="ui-monospace, monospace"
      >
        {gearLabel}
      </text>
      <text x="60" y="30" textAnchor="middle" fontSize="6.5" fill="rgba(255,255,255,0.4)">
        ×1000 rpm
      </text>
    </svg>
  );
}
